//models
import { Tenant } from "models/tenant/tenant";
import React, { FC, useEffect, useMemo, useState } from "react";

import Select from "react-select";
import TableContainer from "common/table-container";
import LoadingComponent from "common/components/loader";

import { AppDispatch } from "slices/store";
import { useAppDispatch, useAppSelector } from "slices/hook";
import { getCoreDataState } from "slices/core-data/reducer";
import { userRolesGetThunk } from "slices/core-data/thunk";
import { getAuthState } from "slices/auth/login/reducer";

import { AssignAdminToTenant, GetSystemUsers, GetTenantUsers } from "api/tenant";

import { AppUtility } from "../../../utility/app-utility";
import { User, UserTenantAdminAssign } from "models/user/user";
import { UserRole } from "models/user/user-role";
import { TenantType, UserRoles } from "models/enums";

interface Props {
  tenant?: Tenant;
}

const TenantAdminUsersComponent: React.FC<Props> = ({ tenant }) => {
  const [data, setData] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [tenantAdmins, setTenantAdmins] = useState<User[]>([]);

  const dispatch: AppDispatch = useAppDispatch();
  const { userRoles } = useAppSelector(getCoreDataState);
  const { loggedInuser } = useAppSelector(getAuthState);
  const [systemTenant, setSystemTenant] = useState(false);

  useEffect(() => {
    const _isSystem = tenant?.tenantTypeId == TenantType.SYSTEM;
    setSystemTenant(_isSystem);
    dispatch(userRolesGetThunk());
  }, []);

  const [userRoleOptions, setUserRoleOptions] = useState<{ value: string; label: string }[]>([
    { value: "", label: "Select Role" },
  ]);

  useEffect(() => {
    const _roles = [UserRoles.SYSTEM_ADMIN, UserRoles.SYSTEM_SUPER_ADMIN, UserRoles.SYSTEM_USER];
    const _data = userRoles
      .filter((r) => _roles.findIndex((x) => x == r.dbKey) >= 0)
      .map((role) => ({
        value: role.id ?? "",
        label: role.name ?? "",
        dbKey: role.dbKey ?? "",
      }));

    setUserRoleOptions([{ value: "", label: "Select Role" }, ..._data]);
  }, [userRoles]);

  const loadSystemUsers = async () => {
    if (tenant) {
      await loadTenantUsers();
      setLoading(true);
      const res = await GetSystemUsers({}, undefined, 100, 1);
      if (!(res?.status?.isError ?? false) && res?.data) {
        const _sysUsers = res?.data ?? [];
        _sysUsers.forEach((itm, index) => {
          const _tnUser = tenantAdmins.find((a) => a.id == itm.id);
          itm.adminPortalRole = itm.activeRole;
          itm.activeRole = _tnUser?.activeRole;
        });
        setData(_sysUsers);
        setTotal(res?.totalResultCount ?? 0);
      } else {
        setData([]);
        setTotal(0);
      }
      setLoading(false);
    }
  };

  const loadTenantUsers = async () => {
    if (tenant) {
      setLoading(true);
      const res = await GetTenantUsers(
        tenant?.id ?? "",
        {
          userRoles: ["SYSTEM_SUPER_ADMIN", "SYSTEM_ADMIN", "SYSTEM_USER"],
        },
        undefined,
        100,
        1
      );
      if (!(res?.status?.isError ?? false) && res?.data) {
        setTenantAdmins(res?.data ?? []);
      } else {
        setTenantAdmins([]);
      }
      // setLoading(false);
    }
  };

  const assignUsersToTenant = async (userTenant: UserTenantAdminAssign) => {
    if (tenant) {
      setLoading(true);
      const res = await AssignAdminToTenant(tenant?.id ?? "", userTenant);
      if (!(res?.status?.isError ?? false) && res?.data) {
        setSuccess("updated successfully");
      } else {
        setError("Failed to assign user");
        loadSystemUsers();
      }
      setLoading(false);
      setTimeout(() => {
        setError("");
        setSuccess("");
      }, 3000);
    }
  };

  // Get Data
  useEffect(() => {
    loadSystemUsers();
  }, [tenant]);

  const columns = useMemo(
    () => [
      {
        header: "First Name",
        accessorKey: "firstName",
        enableColumnFilter: false,
      },
      {
        header: "Last Name",
        accessorKey: "lastName",
        enableColumnFilter: false,
      },
      {
        header: "Username",
        accessorKey: "username",
        enableColumnFilter: false,
      },
      {
        header: "Contact Info",
        accessorKey: "phone",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            <span>Phone: {cell.row.original.phone}</span>
            <br></br>
            <span>Email: {cell.row.original.email}</span>
          </>
        ),
      },
      {
        header: "Role",
        accessor: "role",
        cell: (cell: any) => {
          const [selectedRole, setSelectedRole] = useState<{ value: string; label: string } | null>(
            userRoleOptions.find((option) => option.value === cell.row.original.activeRole?.id) || null
          );

          const handleRoleChange = (selectedOption: { value: string; label: string } | null) => {
            // Assuming you have some method to update the role in your data
            console.log("Selected role for user:", cell.row.original.username, selectedOption);
            // Example API call or state update
            // api.updateUserRole(row.original.id, selectedOption.value);
            if (selectedOption) {
              setSelectedRole(selectedOption);
              if (!cell.row.original.activeRole) {
                cell.row.original.activeRole = {};
              }
              cell.row.original.activeRole.id = selectedOption.value;
              cell.row.original.activeRole.name = selectedOption.label;
            }
          };

          return (
            <>
              <Select
                className="border-slate-200 focus:outline-none focus:border-custom-500"
                options={userRoleOptions}
                isSearchable={false}
                menuPortalTarget={document.body} // Render the menu in the body
                onChange={handleRoleChange}
                value={selectedRole}
              />
            </>
          );
        },
      },
      {
        header: "Actions",
        id: "actions",
        cell: (cell: any) => {
          const handleUpdate = () => {
            // Call your API to update the role here
            console.log("Updated role for user:", cell.row.original.username, cell.row.original.activeRole);
            // Example API call
            // api.updateUserRole(row.original.id, row.original.role);
            assignUsersToTenant({
              user: { id: cell.row.original.id ?? "" },
              role: cell.row.original.activeRole,
            });
          };

          return;
          {
            (!systemTenant || loggedInuser?.activeRole?.name == UserRoles.SYSTEM_SUPER_ADMIN) && (
              <button
                onClick={handleUpdate}
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                Update
              </button>
            );
          }
        },
      },
    ],
    [userRoleOptions]
  );

  return (
    <>
      {loading ? (
        <LoadingComponent></LoadingComponent>
      ) : (
        <div className="overflow-x-auto mt-2">
          {data && data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns || []}
              data={data || []}
              totalCount={total}
              customPageSize={10}
              divclassName="overflow-x-auto"
              tableclassName="w-full whitespace-nowrap h-full"
              theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
              thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
              tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
              PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
            />
          ) : (
            <div className="noresult">
              <div className="py-6 text-center">
                {/*   <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" /> */}
                <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                <p className="mb-0 text-slate-500 dark:text-zink-200">
                  We've searched users matched to your criteria, We did not find any.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {success && (
        <div
          className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
          id="successAlert"
        >
          <b>Success! </b>
          {<>{success}</>}
        </div>
      )}
      {error && (
        <div
          className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
          id="successAlert"
        >
          <>{error}</>
        </div>
      )}
    </>
  );
};

export default TenantAdminUsersComponent;
